// External Imports
import { Fragment, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";

/** ========================================================================
 * * SplitInvoiceModal Component
 * This component renders a form to split an invoice.
 * ========================================================================== */

export default function SplitInvoiceModel({
  open,
  setOpen,
  payment,
  onComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [partials, setPartials] = useState(2);

  /* ====================== REFUND PAYMENT ====================== */
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        paymentId: payment.id,
        reason: e.target.reason.value,
        partials: [],
      };
      Array.from({ length: partials }).forEach((_, idx) => {
        const partial = {
          amount: parseFloat(e.target[`amount-${idx}`].value),
          description: e.target[`description-${idx}`].value,
        };
        payload.partials.push(partial);
      });
      await requestAdminAPI("split-invoice", payload);
      onComplete && onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== HANDLER SPLITS ====================== */
  const handleSplitsChange = (type) => {
    if (type === "add" && partials < 4) setPartials((prev) => prev + 1);
    else if (type === "remove" && partials > 2) setPartials((prev) => prev - 1);
  };

  if (!payment) return null;
  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[45vw]">
        <Heading
          title="Split Invoice"
          text="Invoices can be split into max 4 parts and min 2 parts. The sum of partial amounts must equal the total amount. 
          Taxes are calculated automatically based on ratio of each partial and original tax amount. WARNING: This action cannot be undone."
          isBackArrow={false}
          id={payment?.id}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <form onSubmit={submitHandler} className="info lg:grid-cols-3">
          <DetailPair
            label="Customer"
            value={`${payment.customer?.firstName} ${payment.customer?.lastName}`}
          />
          <DetailPair
            label="Booking"
            value={
              payment.booking
                ? `${payment.booking?.vehicle.year} ${payment.booking?.vehicle?.make?.name} ${payment.booking?.vehicle?.model}`
                : "-"
            }
          />

          <DetailPair
            label="Total Amount"
            classes="font-semibold text-xl"
            value={`$${payment.amount?.toFixed(2)}`}
          />

          {Array.from({ length: partials }).map((_, idx) => (
            <Fragment key={idx}>
              <DetailPair
                label={`Partial ${idx + 1} Amount`}
                edit={true}
                inputOptions={{
                  required: true,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  name: `amount-${idx}`,
                }}
              />
              <div className="lg:col-span-2">
                <DetailPair
                  label="Description"
                  edit={true}
                  inputOptions={{
                    required: true,
                    name: `description-${idx}`,
                    defaultValue: `Partial ${idx + 1}: ${payment.description}`,
                  }}
                />
              </div>
            </Fragment>
          ))}

          {partials > 2 && (
            <div
              onClick={() => handleSplitsChange("remove")}
              className="lg:col-start-2 btn-warning"
            >
              Remove Partial
            </div>
          )}
          <div
            onClick={() => handleSplitsChange("add")}
            className="lg:col-start-3 btn-secondary"
          >
            Add Partial
          </div>

          <div className="lg:col-span-3">
            <DetailPair
              label="Reason for Split"
              edit={true}
              inputOptions={{
                required: true,
                defaultValue: "Requested By Customer.",
                name: "reason",
              }}
            />
          </div>

          {loading ? (
            <div className="mx-auto lg:col-span-3">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2 lg:col-span-3">
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <button type="submit" className="btn-primary">
                Split Invoice
              </button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
}
